import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Plus, Trash2, Save, Loader2, Video as VideoIcon, ExternalLink } from "lucide-react";
import { TOKEN_KEY } from "@/pages/AdminLogin";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PROVIDER_BADGE = {
  youtube: { color: "#FF0000", label: "YouTube" },
  vimeo: { color: "#1ab7ea", label: "Vimeo" },
  instagram: { color: "#E4405F", label: "Instagram" },
  tiktok: { color: "#000000", label: "TikTok" },
  facebook: { color: "#1877F2", label: "Facebook" },
  loom: { color: "#625DF5", label: "Loom" },
  mp4: { color: "#A8A58F", label: "MP4" },
};

const PLACEMENTS = [
  { v: "backend_only", l: "Nur im Backend" },
  { v: "startseite", l: "Startseite" },
  { v: "ueber", l: "Über-Mich Section" },
  { v: "coaching", l: "Coaching Section" },
  { v: "galerie", l: "Galerie" },
  { v: "referenzen", l: "Referenzen" },
  { v: "eigene_video_section", l: "Video Section" },
  { v: "landingpage", l: "Landingpage" },
];

const emptyVideo = { url: "", title: "", description: "", category: "", cta_text: "", cta_link: "", placement: "backend_only", active: true, order: 0, thumbnail: "" };

const AdminVideos = () => {
  const navigate = useNavigate();
  const token = typeof window !== "undefined" ? localStorage.getItem(TOKEN_KEY) : null;
  const api = useMemo(() => axios.create({ baseURL: API, headers: token ? { Authorization: `Bearer ${token}` } : {} }), [token]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [detect, setDetect] = useState(null);

  const fetch = async () => {
    if (!token) { navigate("/admin/login"); return; }
    setLoading(true);
    try { const r = await api.get("/admin/videos"); setVideos(r.data); }
    catch (e) { if ([401,403].includes(e?.response?.status)) navigate("/admin/login"); }
    finally { setLoading(false); }
  };
  useEffect(() => { document.title = "CRM · Videos"; fetch(); }, []); // eslint-disable-line

  const onUrlBlur = async (url) => {
    if (!url) return;
    try { const r = await api.post("/admin/videos/detect", { url }); setDetect(r.data); }
    catch { setDetect({ provider: "unknown" }); }
  };

  const save = async () => {
    if (!editing.url) { toast.error("URL fehlt."); return; }
    setSaving(true);
    try {
      if (editing.id) await api.patch(`/admin/videos/${editing.id}`, editing);
      else await api.post("/admin/videos", editing);
      toast.success("Video gespeichert.");
      setEditing(null); setDetect(null); fetch();
    } catch (e) { toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen."); }
    finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Video wirklich löschen?")) return;
    try { await api.delete(`/admin/videos/${id}`); toast.success("Gelöscht."); fetch(); }
    catch { toast.error("Fehlgeschlagen."); }
  };

  const toggleActive = async (v) => {
    try { await api.patch(`/admin/videos/${v.id}`, { ...v, active: !v.active }); fetch(); } catch { toast.error("Fehler."); }
  };

  return (
    <div className="min-h-screen bg-[#5C6B49] text-[#F3F1E8] py-8 px-4 sm:px-6 lg:px-8" data-testid="admin-videos-page">
      <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <Link to="/admin/leads" className="inline-flex items-center gap-2 text-[#C9C6B4] hover:text-[#F3F1E8] text-xs font-bold uppercase tracking-wider mb-2">
              <ArrowLeft size={12}/> CRM
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Videos</h1>
            <p className="text-[#A8A58F] text-sm mt-1">{videos.length} Videos · YouTube, Vimeo, Instagram, TikTok, MP4</p>
          </div>
          <button onClick={() => { setEditing({ ...emptyVideo }); setDetect(null); }} className="inline-flex items-center gap-2 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] font-extrabold uppercase tracking-wider rounded-full px-5 py-2.5 text-sm" data-testid="new-video-btn">
            <Plus size={14}/> Video hinzufügen
          </button>
        </div>

        {loading ? <div className="text-[#C9C6B4]">Lade…</div> : videos.length === 0 ? (
          <div className="rounded-2xl border border-white/12 bg-[#4E5A3B]/50 p-10 text-center text-[#C9C6B4]">
            Noch keine Videos. „Video hinzufügen" klicken, URL einfügen – Anbieter wird automatisch erkannt.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {videos.map(v => {
              const badge = PROVIDER_BADGE[v.provider] || { color: "#A8A58F", label: v.provider };
              return (
                <div key={v.id} className="rounded-2xl border border-white/12 bg-[#4E5A3B]/50 overflow-hidden" data-testid={`video-${v.id}`}>
                  {v.thumbnail && <div className="aspect-video bg-black/40 overflow-hidden"><img src={v.thumbnail} alt="" className="w-full h-full object-cover"/></div>}
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2 mb-2">
                      <div className="min-w-0">
                        <div className="font-bold text-[#F3F1E8] truncate">{v.title || "Ohne Titel"}</div>
                        <a href={v.url} target="_blank" rel="noopener noreferrer" className="text-[11px] text-[#A8A58F] hover:text-[#E8E5D0] inline-flex items-center gap-1 truncate">
                          <ExternalLink size={10}/> {v.url}
                        </a>
                      </div>
                      <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full text-white shrink-0" style={{background: badge.color}}>{badge.label}</span>
                    </div>
                    {v.description && <p className="text-xs text-[#C9C6B4] mb-2 line-clamp-2">{v.description}</p>}
                    <div className="flex items-center justify-between gap-2 mt-3">
                      <label className="inline-flex items-center gap-2 text-xs text-[#C9C6B4] cursor-pointer">
                        <input type="checkbox" checked={v.active} onChange={() => toggleActive(v)} data-testid={`toggle-active-${v.id}`}/>
                        {v.active ? "Aktiv" : "Inaktiv"}
                      </label>
                      <div className="flex gap-1">
                        <button onClick={() => { setEditing(v); setDetect({ provider: v.provider }); }} className="text-xs font-bold border border-white/15 hover:bg-black/[0.15] rounded-lg px-3 py-1.5">Bearbeiten</button>
                        <button onClick={() => del(v.id)} className="text-xs font-bold bg-red-500/20 border border-red-500/40 hover:bg-red-500/30 text-red-200 rounded-lg px-3 py-1.5" data-testid={`delete-${v.id}`}><Trash2 size={12}/></button>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-[#5C6B49] border border-white/15 rounded-2xl max-w-xl w-full max-h-[92vh] overflow-y-auto p-6" onClick={e => e.stopPropagation()} data-testid="video-editor">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-xl font-black">{editing.id ? "Video bearbeiten" : "Neues Video"}</h2>
              <button onClick={() => setEditing(null)} className="text-[#C9C6B4]">✕</button>
            </div>
            <div className="space-y-3">
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">Video-URL</label>
                <input type="url" value={editing.url} onChange={e => setEditing({ ...editing, url: e.target.value })} onBlur={e => onUrlBlur(e.target.value)}
                  placeholder="https://www.youtube.com/watch?v=... oder Vimeo/Instagram/TikTok/MP4"
                  className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8] placeholder:text-[#A8A58F]" data-testid="video-url"/>
                {detect && (
                  <div className="mt-1 text-[11px] text-[#C9C6B4]">
                    {detect.provider === "unknown" ? <span className="text-amber-300">⚠ Anbieter nicht erkannt</span> : <span>Erkannt: <strong className="text-[#E8E5D0]">{PROVIDER_BADGE[detect.provider]?.label || detect.provider}</strong></span>}
                  </div>
                )}
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">Titel</label>
                <input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]"/>
              </div>
              <div>
                <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">Beschreibung</label>
                <textarea rows={3} value={editing.description} onChange={e => setEditing({ ...editing, description: e.target.value })} className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]"/>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">Kategorie</label>
                  <input value={editing.category} onChange={e => setEditing({ ...editing, category: e.target.value })} placeholder="z. B. Coaching" className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]"/>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">Platzierung</label>
                  <select value={editing.placement} onChange={e => setEditing({ ...editing, placement: e.target.value })} className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]">
                    {PLACEMENTS.map(p => <option key={p.v} value={p.v}>{p.l}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">CTA-Text</label>
                  <input value={editing.cta_text} onChange={e => setEditing({ ...editing, cta_text: e.target.value })} className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]"/>
                </div>
                <div>
                  <label className="text-[11px] font-bold uppercase tracking-wider text-[#A8A58F] mb-1 block">CTA-Link</label>
                  <input value={editing.cta_link} onChange={e => setEditing({ ...editing, cta_link: e.target.value })} className="w-full bg-[#4E5A3B]/60 border border-white/15 rounded-lg px-3 py-2 text-sm text-[#F3F1E8]"/>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm text-[#C9C6B4]">
                <input type="checkbox" checked={editing.active} onChange={e => setEditing({ ...editing, active: e.target.checked })} data-testid="editor-active"/>
                Aktiv (auf Website sichtbar)
              </label>
            </div>
            <div className="flex gap-2 mt-5">
              <button onClick={() => setEditing(null)} className="flex-1 border border-white/15 hover:bg-black/[0.15] text-sm font-bold rounded-lg px-4 py-2.5">Abbrechen</button>
              <button onClick={save} disabled={saving} className="flex-1 bg-[#E8E5D0] hover:bg-[#D6D3BF] text-[#3A4230] text-sm font-bold rounded-lg px-4 py-2.5 disabled:opacity-50" data-testid="save-video-btn">
                {saving ? <Loader2 size={14} className="animate-spin inline"/> : <Save size={14} className="inline mr-1"/>} Speichern
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVideos;
